/**
 * Status-condition Phaser sprite tint constants. Mirrors DESIGN.md's
 * --status-* CSS tokens (locked 2026-05-13) as 0xRRGGBB number literals
 * so Phaser.GameObjects.Image.setTint() can consume them. Phaser doesn't
 * read CSS variables, hence the explicit baking.
 *
 *  ─── Sync rule (eng review 2026-05-13) ────────────────────────────
 *  If a token in apps/game/src/styles/tokens.css changes (a future
 *  /design-review pass tunes a shade), update the matching hex below.
 *  No automatic linkage — this module IS the contract between the
 *  CSS-tokens layer (DOM glass: orb status pip) and the Phaser sprite
 *  layer (battle sprite tint). Tests assert numeric equality of the
 *  derived hex against tokens.css; keep them in lockstep.
 *
 *  ─── tox → psn collapse ───────────────────────────────────────────
 *  Sim emits `tox` distinct from `psn`; v0.1 PartyMember.status carries
 *  both. Cartridge tints both the same purple (visual collapse); the
 *  PartyMember field keeps them distinct for mechanical accuracy
 *  (badly-poisoned damage scaling lands in a future expansion).
 */
/**
 * Status-condition value type. Authoritative shape used by both:
 *   - PartyMember.status (lives in runState; persists across battles)
 *   - sim-internal pokemon.status (we mutate it in runInteractiveBattle)
 *
 * The field is `null` when no condition is active; the five (six with
 * tox) string variants mirror sim's emit values verbatim so the
 * status-persistence round-trip is byte-stable.
 */
export type PartyMemberStatus =
  | 'psn'
  | 'tox'
  | 'par'
  | 'brn'
  | 'slp'
  | 'frz'
  | null;

/** Hex tint values keyed by PartyMember.status. `null` returns no tint
 *  (Phaser.setTint(0xffffff) restores the original sprite color). */
export const STATUS_TINT_HEX: Record<NonNullable<PartyMemberStatus>, number> = {
  psn: 0xa14eb3,
  tox: 0xa14eb3, // cartridge convention: tox shares psn's purple
  par: 0xe5c13d,
  brn: 0xe8633a,
  slp: 0x7891b5,
  frz: 0x9ed9e8,
};

/** Convenience getter that returns the "no tint" white value for null /
 *  unknown statuses, so callers can write
 *    sprite.setTint(statusTintHex(member.status))
 *  without a null-coalescing dance at every call site. */
export function statusTintHex(status: PartyMemberStatus): number {
  if (status === null) return 0xffffff;
  return STATUS_TINT_HEX[status];
}
